/**
 * Modal para crear etiqueta de ruta de entrega
 * Permite seleccionar ruta, empleado, bultos y comentario
 * Genera, registra e imprime etiqueta de envío
 */
import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import NumbersIcon from "@mui/icons-material/Numbers";
import NotesIcon from "@mui/icons-material/Notes";
import { FieldLabel } from "../ui";
import { StateContext } from "../../../../components/context/StateProvider";
import DeliveryService from "../../../../services/delivery.service";
import OrderService from "../../../../services/order.service";
import StickerService from "../../../../services/sticker.service";
import SalesService from "../../../../services/sales.service";
import Sticker from "../../../../models/Sticker";
import Utils from "../../../../utils/Utils";
import Toasts from "../../../../components/notificacion/Toasts";

const ModalEtiquetaRuta = ({
    show,
    onClose,
    employees = [],
    onEtiquetaCreada,
    onMostrarIncidencia
}) => {
    const { state, updateState } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [deliveries, setDeliveries] = useState([]);
    const [deliveriesOptions, setDeliveriesOptions] = useState([]);
    const [employeesOptions, setEmployeesOptions] = useState([]);
    const [transports, setTransports] = useState([]);
    const [imprimiendo, setImprimiendo] = useState(false);
    const [cargando, setCargando] = useState(false);

    const [formData, setFormData] = useState({
        delivery: '0',
        employee: '0',
        packages: 1,
        comment: '',
        type: 0,
        deliveryObject: undefined
    });

    // Cargar datos al abrir el modal
    // DECISION: depender solo de `show`. Depender de state.order (objeto que cambia de
    // referencia con cada updateState del padre) recargaba los datos y reseteaba el
    // formulario mientras el usuario lo rellenaba.
    useEffect(() => {
        if (!show) return;

        const cargarDatos = async () => {
            setCargando(true);
            try {
                // Cargar rutas
                const deliveriesData = await DeliveryService.getDeliveries(state.year) || [];
                setDeliveries(deliveriesData);

                // Opciones de rutas (solo las que están en panel)
                const rutasOpciones = deliveriesData
                    .filter(d => d.panel === true)
                    .map(d => ({
                        label: d.name + ' - ' + Utils.formatDateWithoutHour(Date.parse(d.date)),
                        value: String(d.id),
                    }));
                setDeliveriesOptions(rutasOpciones);

                // Opciones de empleados
                const empleadosOpciones = employees.map(emp => ({
                    label: emp.nombre,
                    value: String(emp.id)
                }));
                setEmployeesOptions(empleadosOpciones);

                // Cargar transportes
                const transportesData = await DeliveryService.getTransports();
                setTransports(transportesData);

                // Pre-poblar formulario si ya existe sticker/delivery
                const deliveryActual = state.order?.delivery?.id;
                setFormData({
                    delivery: deliveryActual || '0',
                    employee: state.order?.sticker?.employee || '0',
                    packages: state.order?.sticker?.packages || 1,
                    comment: state.order?.sticker?.comment || '',
                    type: state.order?.sticker?.type || 0,
                    deliveryObject: deliveryActual
                        ? deliveriesData.find(d => String(d.id) === String(deliveryActual))
                        : undefined
                });
            } catch (error) {
                console.error('Error al cargar datos del modal:', error);
                const t = Toasts.generateToast('No se pudieron cargar los datos de la ruta', 'error');
                updateState({ toasts: [...state.toasts, t] });
            } finally {
                setCargando(false);
            }
        };

        cargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Si cambia la ruta, actualizar deliveryObject
            if (field === 'delivery') {
                updated.deliveryObject = deliveries.find(d => String(d.id) === String(value));
            }

            return updated;
        });
    };

    const handleSubmit = async () => {
        setImprimiendo(true);

        // Validaciones
        if (formData.delivery === '0') {
            const t = Toasts.generateToast('La ruta es obligatoria', 'error');
            updateState({ toasts: [...state.toasts, t] });
            setImprimiendo(false);
            return;
        }
        if (formData.employee === '0') {
            const t = Toasts.generateToast('El empleado es obligatorio', 'error');
            updateState({ toasts: [...state.toasts, t] });
            setImprimiendo(false);
            return;
        }
        const pkg = Number(formData.packages);
        if (!pkg || pkg < 1) {
            const t = Toasts.generateToast('Los bultos deben ser ≥ 1', 'error');
            updateState({ toasts: [...state.toasts, t] });
            setImprimiendo(false);
            return;
        }

        try {
            const orden = Utils.getOrder(state.order.num);
            const delivery = deliveries.find(d => String(d.id) === String(formData.delivery));
            const employee = employees.find(d => String(d.id) === String(formData.employee));
            const transport = transports.find(d => String(d.CODIGO) === String(delivery.transport));
            const customer = await OrderService.getCustomerOrder(orden.serie, orden.order, delivery.year);

            // Crear objeto Sticker (contrato del modelo Sticker / StickerService.registerSticker)
            const etiqueta = new Sticker();
            etiqueta.id = state.order.num;
            etiqueta.serie = orden.serie;
            etiqueta.order = orden.order;
            etiqueta.packages = formData.packages;
            etiqueta.weight = 0;
            etiqueta.counterDelivery = delivery.counter;
            etiqueta.delivery = delivery.id;
            etiqueta.dateDelivery = delivery.date;
            etiqueta.nameDelivery = delivery.name;
            etiqueta.agency = delivery.transport;
            etiqueta.nameAgency = transport?.NOMBRE;
            etiqueta.created = new Date();
            etiqueta.employee = employee.id;
            etiqueta.nameEmployee = employee.nombre;
            etiqueta.comment = formData.comment;
            etiqueta.departamento = formData.type;
            etiqueta.expedicionAsigna = state.order?.sticker?.expedicionAsigna || null;
            etiqueta.zona = state.order?.sticker?.zona || null;

            if (customer.length > 0) {
                etiqueta.customer = customer[0].ClientesModel.NOMBRE;
                etiqueta.id_customer = customer[0].CLIENTE;
                etiqueta.way_pay = customer[0].FPagModel.NOMBRE;
                etiqueta.listCustomerAdress = customer[0].ClientesModel.EnviCliModels;
                etiqueta.tradename = customer[0].ClientesModel.NOMBRE2;
                etiqueta.phonesDefault = customer[0].ClientesModel.TelfCliModels;

                if (!Utils.isEmpty(customer[0].DIRECCION)) {
                    etiqueta.name = (!Utils.isFullEmpty(customer[0].DIRECCION.NOMBRE) ? customer[0].DIRECCION.NOMBRE : (!Utils.isFullEmpty(customer[0].DIRECCION.NOMBRE2) ? customer[0].DIRECCION.NOMBRE2 : customer[0].ClientesModel.NOMBRE));
                    etiqueta.address = (customer[0].DIRECCION.DIRECCION === null ? '' : customer[0].DIRECCION.DIRECCION);
                    etiqueta.cp = (customer[0].DIRECCION.CODPOS === null ? '' : customer[0].DIRECCION.CODPOS);
                    etiqueta.county = (customer[0].DIRECCION.POBLACION === null ? '' : customer[0].DIRECCION.POBLACION);
                    etiqueta.state = (customer[0].DIRECCION.PROVINCIA === null ? '' : customer[0].DIRECCION.PROVINCIA);
                    etiqueta.phone = (!Utils.isFullEmpty(customer[0].DIRECCION.TELEFONO) ? customer[0].DIRECCION.TELEFONO : (customer[0].ClientesModel.TelfCliModels.length > 0 && !Utils.isFullEmpty(customer[0].ClientesModel.TelfCliModels[0]?.TELEFONO) ? customer[0].ClientesModel.TelfCliModels[0]?.TELEFONO : ''));
                    etiqueta.linea = (customer[0].DIRECCION.LINEA === null ? '' : customer[0].DIRECCION.LINEA);
                }
            }

            // Registrar etiqueta
            await StickerService.registerSticker(etiqueta);

            const t = Toasts.generateToast('Se ha creado la etiqueta correctamente', 'success');
            updateState({ toasts: [...state.toasts, t] });

            // Actualizar estado local
            state.order.sticker = etiqueta;
            state.order.delivery = delivery;
            updateState({ order: state.order });

            try {
                // Comprobar series y punteos de todos los productos antes de imprimir
                await SalesService.checkSeriesProductsOrder(state.order.num, state.year);
                await SalesService.checkProductsRegisteredOrder(state.order.num, state.year);

                // Imprimir etiqueta (el padre gestiona la impresión Asigna/Msoft con la ruta)
                if (onEtiquetaCreada) {
                    onEtiquetaCreada(etiqueta, delivery);
                }
            } catch (error) {
                if (error.response?.data?.code === 422) {
                    // Productos sin puntear: abrir incidencia (el padre construye el objeto)
                    if (onMostrarIncidencia) {
                        onMostrarIncidencia(
                            etiqueta,
                            'No se han punteado todos los productos del albarán <strong>' + etiqueta.serie + '-' + etiqueta.order + '</strong>'
                        );
                    }
                } else {
                    const resMessage = error.response?.data?.message || error.message || error.toString();
                    const errors = [Toasts.generateToast(resMessage, 'error')];
                    if (error.response?.data?.details) {
                        for (let i = 0; i < error.response.data.details.length; i++) {
                            errors.push(Toasts.generateToast(error.response.data.details[i], 'warning'));
                        }
                    }
                    updateState({ toasts: [...state.toasts, ...errors] });
                }
            }
        } catch (error) {
            const t = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, t] });
        } finally {
            setImprimiendo(false);
            onClose();
        }
    };

    const handleClose = () => {
        if (!imprimiendo) {
            onClose();
        }
    };

    return (
        <Dialog
            open={show}
            onClose={imprimiendo ? undefined : handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2,
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: { xs: 1, sm: 2 },
                pb: { xs: 1.5, sm: 2 },
                pt: { xs: 2, sm: 2.5 },
                px: { xs: 2, sm: 3 },
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <PrintIcon sx={{ color: "#cc071e", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" },
                        }}
                    >
                        Etiqueta de ruta
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={imprimiendo}
                    size="small"
                    sx={{ color: "#6c757d" }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Ruta */}
                    <Box>
                        <FieldLabel icon={LocalShippingIcon}>Ruta *</FieldLabel>
                        <FormControl fullWidth>
                            <Select
                                value={formData.delivery}
                                onChange={(e) => handleChange('delivery', e.target.value)}
                                disabled={cargando}
                            >
                                <MenuItem value="0" disabled>
                                    Elige ruta
                                </MenuItem>
                                {deliveriesOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Empleado */}
                    <Box>
                        <FieldLabel icon={PersonIcon}>Empleado *</FieldLabel>
                        <FormControl fullWidth>
                            <Select
                                value={formData.employee}
                                onChange={(e) => handleChange('employee', e.target.value)}
                                disabled={cargando}
                            >
                                <MenuItem value="0" disabled>
                                    Elige empleado
                                </MenuItem>
                                {employeesOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Tipo */}
                    <Box>
                        <FieldLabel icon={Inventory2Icon}>Tipo</FieldLabel>
                        <FormControl fullWidth>
                            <Select
                                value={formData.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                                disabled={cargando}
                            >
                                <MenuItem value={0}>Bulto</MenuItem>
                                <MenuItem value={20}>Palet</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Bultos */}
                    <Box>
                        <FieldLabel icon={NumbersIcon}>Bultos *</FieldLabel>
                        <TextField
                            type="number"
                            fullWidth
                            inputProps={{ min: 1 }}
                            value={formData.packages}
                            onChange={(e) => handleChange('packages', e.target.value)}
                        />
                    </Box>

                    {/* Comentario */}
                    <Box>
                        <FieldLabel icon={NotesIcon}>Comentario</FieldLabel>
                        <TextField
                            placeholder="Añadir un comentario u observación"
                            fullWidth
                            value={formData.comment}
                            onChange={(e) => handleChange('comment', e.target.value)}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={imprimiendo}
                    sx={{
                        borderColor: "#6c757d",
                        color: "#6c757d",
                        "&:hover": {
                            borderColor: "#495057",
                            backgroundColor: "rgba(108, 117, 125, 0.04)"
                        }
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={imprimiendo || cargando}
                    startIcon={imprimiendo ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                    sx={{
                        backgroundColor: "#cc071e",
                        "&:hover": {
                            backgroundColor: "#a30618"
                        },
                        minWidth: 180
                    }}
                >
                    {imprimiendo ? "Imprimiendo..." : "Imprimir etiqueta"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalEtiquetaRuta;
